/**
 * Wires the framework-free {@link SiteTracker} into the public SPA.
 *
 * `site-tracker.ts` owns the queueing/batching rules and takes every browser
 * surface as an injected dependency; this module supplies the real ones, holds
 * the single per-tab tracker instance, and exposes the small set of recording
 * helpers the call sites use:
 *
 * | Requirement | Call site |
 * | --- | --- |
 * | 2.1 page view + referrer origin | {@link useSiteTracker} mount effect, from `App.tsx` |
 * | 2.2 section view | `hashchange` listener in {@link useSiteTracker} |
 * | 2.3 image open | `Gallery.tsx`, when the `Lightbox` opens |
 * | 2.4 click-through | `Nav` links, hero/booking CTAs, footer external links |
 * | 2.5 form started | first field edit in `BookingForm.tsx` |
 * | 2.6 booking submitted | success branch of the `POST /bookings` call |
 * | 2.9 send on page hide | `visibilitychange` listener in {@link useSiteTracker} |
 *
 * Measurement must never be able to affect the visitor (Req 2.12, 14.5), so
 * every helper here is a hard no-throw boundary: construction, event recording
 * and flushing are all wrapped, and a failure simply means the event is lost.
 * Nothing propagates into React rendering or into the booking submit path.
 *
 * Sends stay on their own request path — a `POST` to `${API_BASE_URL}/analytics/events`
 * issued by `sendBeacon`/`keepalive` fetch, never piggybacked on a booking
 * submission or on a render-blocking request (Req 2.11).
 */

import { useEffect } from "react";
import { EngagementEventType } from "@photography-booking/shared";
import { API_BASE_URL } from "../api/client.js";
import { SiteTracker, type SiteTrackerDeps } from "./site-tracker.js";

/** Section reported for a page view that lands without a hash (Req 2.1). */
export const DEFAULT_SECTION_ID = "hero";

/**
 * Reads the section identifier out of a location hash: `"#gallery"` →
 * `"gallery"`, an empty or unusable hash → {@link DEFAULT_SECTION_ID}. Any
 * query/extra path noise after the id is dropped so the same section always
 * reports the same identifier.
 */
export function sectionIdFromHash(hash: string | undefined): string {
  const raw = (hash ?? "").replace(/^#\/?/, "").split(/[?&/]/)[0] ?? "";
  return raw === "" ? DEFAULT_SECTION_ID : raw;
}

/**
 * Reduces a full referrer URL to its origin, which is all that is ever sent
 * (Req 2.1, 3.2). A missing or unparseable referrer is a direct visit (`""`).
 */
export function referrerOriginOf(referrer: string | undefined): string {
  if (!referrer) {
    return "";
  }
  try {
    return new URL(referrer).origin;
  } catch {
    return "";
  }
}

/** In-memory stand-in for a `sessionStorage` that is missing or blocked. */
const memoryStore = new Map<string, string>();

/**
 * The real `sessionStorage`, or an in-memory map when the browser refuses to
 * hand it over (Safari private mode, blocked-storage policies). No cookie
 * fallback is ever attempted (Req 3.10).
 */
function resolveSessionStore(): Pick<Storage, "getItem" | "setItem"> {
  try {
    const store = globalThis.sessionStorage;
    if (store) {
      return store;
    }
  } catch {
    // Fall through to the in-memory store.
  }
  return {
    getItem: (key: string) => memoryStore.get(key) ?? null,
    setItem: (key: string, value: string) => {
      memoryStore.set(key, value);
    },
  };
}

/** Builds the browser-backed dependencies for the live tracker. */
function browserDeps(): SiteTrackerDeps {
  return {
    apiBaseUrl: API_BASE_URL,
    sessionStore: resolveSessionStore(),
    send: (url, body) => {
      const beacon = globalThis.navigator?.sendBeacon;
      if (typeof beacon !== "function") {
        return false;
      }
      return beacon.call(globalThis.navigator, url, body) === true;
    },
    now: () => Date.now(),
    randomId: () => globalThis.crypto?.randomUUID?.() ?? "",
    screenWidth: () => globalThis.window?.innerWidth ?? 0,
    privacyControlEnabled: () =>
      (globalThis.navigator as { globalPrivacyControl?: boolean } | undefined)
        ?.globalPrivacyControl === true,
    referrerOrigin: () => referrerOriginOf(globalThis.document?.referrer),
  };
}

/** The one tracker for this tab; `null` until first use or if it can't build. */
let instance: SiteTracker | null = null;

/** Set once construction has been attempted, successfully or not. */
let instanceResolved = false;

/** Guards the single page-view event per loaded document (Req 2.1). */
let pageViewRecorded = false;

/**
 * Returns the shared tracker, constructing it on first use. Returns `null` when
 * construction fails, in which case every helper below becomes a no-op.
 */
export function getSiteTracker(): SiteTracker | null {
  if (!instanceResolved) {
    instanceResolved = true;
    try {
      instance = new SiteTracker(browserDeps());
    } catch {
      instance = null;
    }
  }
  return instance;
}

/**
 * Drops the shared tracker and the once-per-document guards. Test-only seam so
 * a suite can start from a clean tracker.
 */
export function resetSiteTracker(): void {
  instance = null;
  instanceResolved = false;
  pageViewRecorded = false;
  memoryStore.clear();
}

/**
 * Runs `action` against the shared tracker, swallowing everything. This is the
 * boundary that keeps a measurement failure away from rendering and from the
 * booking submit path (Req 2.12, 14.5).
 */
function withTracker(action: (tracker: SiteTracker) => void): void {
  try {
    const tracker = getSiteTracker();
    if (tracker) {
      action(tracker);
    }
  } catch {
    // Measurement is best-effort: a lost event is the worst outcome allowed.
  }
}

/** Records a section view for the section a visitor navigated to (Req 2.2). */
export function trackSectionView(sectionId: string): void {
  withTracker((tracker) => {
    tracker.record(EngagementEventType.SECTION_VIEW, sectionId);
  });
}

/** Records a portfolio image opened in the enlarged view (Req 2.3). */
export function trackImageOpen(imageId: string): void {
  withTracker((tracker) => {
    tracker.record(EngagementEventType.IMAGE_OPEN, imageId);
  });
}

/**
 * Records a click-through on a nav link, a hero/booking CTA or an external
 * footer link (Req 2.4). `targetId` is a stable, human-readable id such as
 * `nav:gallery`, `hero:scroll` or `footer:instagram`.
 */
export function trackClickThrough(targetId: string): void {
  withTracker((tracker) => {
    tracker.record(EngagementEventType.CLICK_THROUGH, targetId);
  });
}

/**
 * Records the first booking-form field edit of the session; later calls are
 * no-ops, so the event counts sessions that started the form (Req 2.5).
 */
export function trackFormStarted(): void {
  withTracker((tracker) => {
    tracker.recordFormStarted();
  });
}

/** Records a booking inquiry that the API accepted (Req 2.6). */
export function trackBookingSubmitted(): void {
  withTracker((tracker) => {
    tracker.record(EngagementEventType.BOOKING_SUBMITTED);
  });
}

/**
 * Mounts the SPA-wide tracking: the page-view event for this load, the
 * `hashchange` → section-view listener (Req 2.2) and the `visibilitychange` →
 * hidden flush that gets pending events out before the page goes away
 * (Req 2.9). Called once, from `App.tsx`.
 *
 * The page view is recorded at most once per loaded document, so a remount (or
 * React's development double-invoked effect) cannot inflate the site-view
 * count. No flush happens on unmount: sends are allowed only at the batch
 * threshold or on page hide (Req 2.10).
 */
export function useSiteTracker(): void {
  useEffect(() => {
    if (!pageViewRecorded) {
      pageViewRecorded = true;
      withTracker((tracker) => {
        tracker.record(
          EngagementEventType.PAGE_VIEW,
          sectionIdFromHash(globalThis.location?.hash),
        );
      });
    }

    const onHashChange = (): void => {
      trackSectionView(sectionIdFromHash(globalThis.location?.hash));
    };
    const onVisibilityChange = (): void => {
      if (globalThis.document?.visibilityState === "hidden") {
        withTracker((tracker) => {
          tracker.flush();
        });
      }
    };

    window.addEventListener("hashchange", onHashChange);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("hashchange", onHashChange);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);
}
